/**
 * StorageInfo Component
 *
 * Displays recipe storage information in a grid layout:
 * - Eat immediately indicator
 * - Fridge life in days
 * - Freezable status
 * - Healthiness rating
 */

interface StorageInfoProps {
  eatImmediately?: boolean;
  fridgeLifeDays?: number | null;
  freezable?: boolean | null;
  healthinessRating?: number | null;
}

// SVG Icons as components
function PlateIcon() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4" />
    </svg>
  );
}

function FridgeIcon() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="4" y1="10" x2="20" y2="10" />
      <line x1="10" y1="6" x2="10" y2="6.01" strokeLinecap="round" />
      <line x1="10" y1="14" x2="10" y2="14.01" strokeLinecap="round" />
    </svg>
  );
}

function SnowflakeIcon() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <line x1="12" y1="2" x2="12" y2="22" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
      <line x1="19.07" y1="4.93" x2="4.93" y2="19.07" />
      <path d="M12 2l2 4-2-1-2 1 2-4" />
      <path d="M12 22l-2-4 2 1 2-1-2 4" />
      <path d="M2 12l4 2-1-2 1-2-4 2" />
      <path d="M22 12l-4-2 1 2-1 2 4-2" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}

export function StorageInfo({
  eatImmediately,
  fridgeLifeDays,
  freezable,
  healthinessRating,
}: StorageInfoProps) {
  const getEatLabel = () => {
    if (eatImmediately) {
      return { text: 'Fresh', color: 'text-red-600' };
    }
    return { text: 'Stores well', color: 'text-green-600' };
  };

  const getFridgeLabel = () => {
    if (fridgeLifeDays === null) {
      return 'N/A';
    }
    if (fridgeLifeDays === 1) {
      return '1 day';
    }
    return `${fridgeLifeDays} days`;
  };

  const getFreezableLabel = () => {
    if (freezable === null) {
      return { text: 'Unknown', color: 'text-gray-500' };
    }
    if (freezable) {
      return { text: 'Yes', color: 'text-green-600' };
    }
    return { text: 'No', color: 'text-red-600' };
  };

  const eatInfo = getEatLabel();
  const freezableInfo = getFreezableLabel();

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="p-3 bg-gray-50 rounded-lg text-center">
        <div className="text-gray-600 mb-1 flex justify-center">
          <PlateIcon />
        </div>
        <div className="text-xs text-gray-400 uppercase tracking-wide">Eat</div>
        <div className={`font-semibold text-sm ${eatInfo.color}`}>{eatInfo.text}</div>
      </div>

      <div className="p-3 bg-gray-50 rounded-lg text-center">
        <div className="text-gray-600 mb-1 flex justify-center">
          <FridgeIcon />
        </div>
        <div className="text-xs text-gray-400 uppercase tracking-wide">Fridge</div>
        <div className="font-semibold text-sm text-gray-900">{getFridgeLabel()}</div>
      </div>

      <div className="p-3 bg-gray-50 rounded-lg text-center">
        <div className="text-blue-500 mb-1 flex justify-center">
          <SnowflakeIcon />
        </div>
        <div className="text-xs text-gray-400 uppercase tracking-wide">Freezable</div>
        <div className={`font-semibold text-sm ${freezableInfo.color}`}>
          {freezableInfo.text}
        </div>
      </div>

      <div className="p-3 bg-gray-50 rounded-lg text-center">
        <div className="text-green-500 mb-1 flex justify-center">
          <HeartIcon />
        </div>
        <div className="text-xs text-gray-400 uppercase tracking-wide">Healthiness</div>
        <div className="font-semibold text-sm text-gray-900">
          {healthinessRating !== null ? `${healthinessRating}/5` : 'N/A'}
        </div>
      </div>
    </div>
  );
}
